// Shared read-only seam between the Rig/Frac chart controller and the data
// table widget: resolves the per-layer attribute filter selections into the
// per-type shape the backends expect. Read-only on the store — layer filter
// writes live in useRigFracLayerFilter.ts (components/map/hooks/adapters).
import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useState } from "react";

import { IS_AWS_ENV } from "constants/app.constants";
import { FRAC_LAYER, RIG_LAYER } from "constants/mapLayers.constants";
import debounce from "lodash/debounce";
import difference from "lodash/difference";
import intersection from "lodash/intersection";
import { useMapStore } from "stores/map";
import { selectLayerFilterSettings } from "stores/map/mapSelectors";
import { RigFracLayerFilter } from "stores/map/mapTypes";
import { normalizeMatchKey } from "utils/color/focusColorSync/operatorKeys";
import {
  AttributeConstraint,
  MAIN_FILTER_CONSTRAINT_ATTRIBUTE_ORDER,
  MainFilterRigFracConstraints
} from "utils/map/mainFilterRigFrac";

import { useMainFilterRigFracConstraints } from "hooks/rigfrac/useMainFilterRigFracConstraints";
import useBetaFeatures from "hooks/useBetaFeatures";

import { RIGFRAC_NO_MATCH_SENTINEL, RigFracFilters } from "api/rigfrac";

import { useRigFracValuesQuery } from "components/project/layers/queries";

const FILTER_FIELDS: readonly (keyof RigFracLayerFilter & keyof RigFracFilters)[] = [
  "operator",
  "county",
  "basin",
  "subBasin",
  "country",
  "stateProvince",
  "locationType"
];

const FILTERS_DEBOUNCE_MS = 300;

// Both chart-svc and data-svc-rs reject a filter field carrying more than
// 1000 values (400 / invalid_argument). Reachability from live UI interaction
// is limited (no select-all, viewport-scoped dropdown options, and an
// exclude-only constraint only expands against a small domain — see
// mergeAttribute's size gate), but a restored saved View carries its
// unvalidated stored displaySettings straight into the request — truncate
// here so that path can't trip the limit and surface a bare request error on
// the table while the map (whose filter never leaves the client) keeps
// working.
const MAX_FILTER_VALUES = 1000;

export interface RigFracTypeFilters {
  /** Sanitized Rigs-layer selection. Always an object; `{}` means no constraint. */
  rigFilters: RigFracFilters;
  /** Sanitized Frac Crews-layer selection. Always an object; `{}` means no constraint. */
  fracFilters: RigFracFilters;
  /**
   * `JSON.stringify([rigFilters, fracFilters])`, debounced (leading edge).
   * The only value safe to use in a dependency array — `rigFilters`/
   * `fracFilters` are memoized on it, so an object dependency would re-fire
   * on every render instead. No-filter value is `"[{},{}]"`.
   */
  filtersKey: string;
}

// Total: the persisted displaySettings blob is unvalidated, so a malformed
// saved View can hold a non-array or non-string entries here — this must
// degrade to `undefined`, never throw.
//
// Sorts + dedupes so an equal-but-reordered selection serializes to the same
// `filtersKey` (otherwise it would spuriously re-request on every surface).
function sanitizeFilterField(value: unknown): Maybe<string[]> {
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter((v): v is string => typeof v === "string");
  const deduped = Array.from(new Set(strings)).sort();
  if (deduped.length === 0) return undefined;
  return deduped.length > MAX_FILTER_VALUES
    ? deduped.slice(0, MAX_FILTER_VALUES)
    : deduped;
}

// Resolves each value to EVERY value domain spelling sharing its
// case-insensitive key (e.g. a well operator bin spelled "APACHE" against the
// rig/frac domain's "Apache") — otherwise passes it through unchanged. A
// missing domain is a no-op, never a block. `/values` is a Mongo `distinct`
// and therefore case-sensitive, so the domain can genuinely hold more than
// one spelling for the same key (e.g. both "APACHE" and "Apache") — resolving
// to only one would silently miss rows spelled the other way.
function resolveDomainCasing(values: string[], domain: Maybe<string[]>): string[] {
  if (!domain) return values;
  const domainSpellingsByKey = new Map<string, string[]>();
  for (const v of domain) {
    const key = normalizeMatchKey(v);
    const existing = domainSpellingsByKey.get(key);
    if (existing) existing.push(v);
    else domainSpellingsByKey.set(key, [v]);
  }
  return values.flatMap((v) => domainSpellingsByKey.get(normalizeMatchKey(v)) ?? [v]);
}

// Shared dedupe-key builder for the warnings below: field name plus the
// sorted value set. Joined with " " rather than "," — a value can itself
// contain a comma (["a,b"] and ["a","b"] would otherwise collide).
function buildFieldWarnKey(field: string, values: readonly string[]): string {
  return `${field} ${[...values].sort().join(" ")}`;
}

// Keyed by field + the exact merged set, so a restored View with an oversized
// selection warns Sentry once per distinct oversized selection rather than on
// every recompute of every mounted chart/table.
const warnedTruncatedKeys = new Set<string>();

function warnMergedFilterTruncated(field: string, merged: string[]): void {
  const key = buildFieldWarnKey(field, merged);
  if (warnedTruncatedKeys.has(key)) return;
  warnedTruncatedKeys.add(key);
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setContext("rigFracFilterMerge", {
      field,
      size: merged.length,
      cap: MAX_FILTER_VALUES
    });
    Sentry.captureMessage("Merged rig/frac filter values truncated at cap");
  });
}

// Keyed by field + the exact exclude set, so the same warning doesn't spam
// Sentry every render for an unchanged constraint but a genuinely new
// exclude-only selection still gets its own report.
const warnedExcludeOnlyKeys = new Set<string>();

function warnExcludeOnlyOnce(field: string, exclude: string[]): void {
  const key = buildFieldWarnKey(field, exclude);
  if (warnedExcludeOnlyKeys.has(key)) return;
  warnedExcludeOnlyKeys.add(key);
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setContext("rigFracFilterMerge", { field });
    Sentry.captureMessage(
      "Rig/frac exclude-only main filter constraint has no list to subtract from — omitting server-side constraint for this field"
    );
  });
}

// Keyed by field + the exact value set being resolved, same rationale as the
// warnings above. Fires only when there was actually something to resolve —
// an empty include/exclude is never worth a breadcrumb.
const warnedCasingUnresolvedKeys = new Set<string>();

function warnCasingUnresolvedOnce(field: string, values: string[]): void {
  const key = buildFieldWarnKey(field, values);
  if (warnedCasingUnresolvedKeys.has(key)) return;
  warnedCasingUnresolvedKeys.add(key);
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setContext("rigFracFilterMerge", { field });
    Sentry.captureMessage(
      'Rig/frac main filter constraint resolved without a value domain — casing mismatches (e.g. "APACHE" vs "Apache") will not be caught'
    );
  });
}

// Wraps resolveDomainCasing with the breadcrumb above: without a domain, the
// resolution below is a no-op, so any values passed in ride straight through
// unresolved — call out that window (query loading, non-beta org, or a
// permanently-null domain after a schema-parse failure) rather than resolving
// silently.
function resolveDomainCasingWithWarning(
  values: string[],
  domain: Maybe<string[]>,
  field: string
): string[] {
  if (!domain && values.length > 0) warnCasingUnresolvedOnce(field, values);
  return resolveDomainCasing(values, domain);
}

/**
 * Merges a layer's selection with a main-filter constraint for one field.
 * - `include`: intersected with the layer selection, or used verbatim if the
 *   layer has none. Values are first resolved to the domain's spelling where
 *   a case-insensitive match exists.
 * - `exclude`: subtracted from whatever concrete list is in hand so far (the
 *   layer selection, or the just-applied include). With no such list, an
 *   exclusion can't be expressed in an `$in`-only contract without the
 *   domain's complement — expanded against the domain when that complement
 *   still fits under the cap (small domains like basin/stateProvince), else
 *   emit no constraint for this field (see module doc; the map still applies
 *   the exclusion client-side).
 */
function mergeAttribute(
  layer: Maybe<string[]>,
  constraint: Maybe<AttributeConstraint>,
  domain: Maybe<string[]>,
  field: string
): Maybe<string[]> {
  let merged = layer;

  if (constraint?.include) {
    const resolvedInclude = resolveDomainCasingWithWarning(
      constraint.include,
      domain,
      field
    );
    merged = merged
      ? intersection(merged, resolvedInclude).sort()
      : [...resolvedInclude].sort();
  }

  if (constraint?.exclude?.length) {
    if (merged === undefined) {
      if (domain && domain.length - constraint.exclude.length <= MAX_FILTER_VALUES) {
        // On Azure the domain is isCurrentlyActive-only, so expanding against
        // it also drops values with no current activity — accepted there for
        // the small domains (basin/stateProvince) this gate lets through. On
        // AWS the domain is historical, so nothing unrelated drops.
        const resolvedExclude = resolveDomainCasingWithWarning(
          constraint.exclude,
          domain,
          field
        );
        merged = difference(domain, resolvedExclude).sort();
      } else {
        warnExcludeOnlyOnce(field, constraint.exclude);
        return undefined;
      }
    } else {
      const resolvedExclude = resolveDomainCasingWithWarning(
        constraint.exclude,
        domain,
        field
      );
      merged = difference(merged, resolvedExclude).sort();
    }
  }

  if (merged === undefined) return undefined;
  if (merged.length === 0) return [RIGFRAC_NO_MATCH_SENTINEL];
  if (merged.length > MAX_FILTER_VALUES) {
    warnMergedFilterTruncated(field, merged);
    merged = merged.slice(0, MAX_FILTER_VALUES);
  }
  return merged;
}

const MAIN_FILTER_ATTRIBUTES: ReadonlySet<string> = new Set(
  MAIN_FILTER_CONSTRAINT_ATTRIBUTE_ORDER
);

function buildSideFilters(
  layer: Maybe<RigFracLayerFilter>,
  constraints: MainFilterRigFracConstraints,
  domainValues: Maybe<Record<string, string[]>>
): RigFracFilters {
  const filters: RigFracFilters = {};

  for (const field of FILTER_FIELDS) {
    // Handled below, alongside the field's main-filter constraint.
    if (MAIN_FILTER_ATTRIBUTES.has(field)) continue;
    const merged = mergeAttribute(
      sanitizeFilterField(layer?.[field]),
      undefined,
      undefined,
      field
    );
    if (merged) filters[field] = merged;
  }

  // Looping the shared exhaustive order (rather than hand-listing attributes)
  // means a new MainFilterRigFracAttribute is handled here automatically
  // instead of silently skipped, as the two map-side consumers were before an
  // earlier fix.
  for (const attribute of MAIN_FILTER_CONSTRAINT_ATTRIBUTE_ORDER) {
    const merged = mergeAttribute(
      sanitizeFilterField(layer?.[attribute]),
      constraints[attribute],
      domainValues?.[attribute],
      attribute
    );
    if (merged) filters[attribute] = merged;
  }

  return filters;
}

// Module-scoped (not per-hook-call) so every mounted chart/table shares one
// timer and settles at the same instant, rather than each instance debouncing
// independently and briefly showing different filter selections. No longer
// cached as a standalone value (each instance now seeds from its own raw
// value — see the listener-count cleanup below) — this Set is purely the
// broadcast mechanism that lets every mounted instance settle in lockstep.
const sharedFiltersKeyListeners = new Set<(key: string) => void>();

function notifySharedFiltersKeyListeners(key: string): void {
  sharedFiltersKeyListeners.forEach((listener) => listener(key));
}

// leading:true applies the first selection in a burst immediately (no latency
// on the common single-tag change — a deliberate gate decision, kept as-is);
// trailing:true then applies the settled remainder once the burst quiets
// down. So a multi-selection made within FILTERS_DEBOUNCE_MS produces TWO
// settled rounds, not one: the first tag alone, then the full selection.
const debouncedSetSharedFiltersKey = debounce(
  notifySharedFiltersKeyListeners,
  FILTERS_DEBOUNCE_MS,
  { leading: true, trailing: true }
);

// Test-only: Jest resets module state between files, not between `it()`
// blocks, so call this in `beforeEach` to avoid leaking state across tests.
export function __resetRigFracTypeFiltersSharedStateForTests(): void {
  debouncedSetSharedFiltersKey.cancel();
  sharedFiltersKeyListeners.clear();
  warnedExcludeOnlyKeys.clear();
  warnedTruncatedKeys.clear();
  warnedCasingUnresolvedKeys.clear();
}

/**
 * Resolves the per-layer filter selections into `{ rigFilters, fracFilters,
 * filtersKey }`. Both objects are always present; an absent/empty field means
 * no constraint.
 */
export function useRigFracTypeFilters(): RigFracTypeFilters {
  const layerFilterSettings = useMapStore(selectLayerFilterSettings);
  const { constraints } = useMainFilterRigFracConstraints();
  const { hasFeature } = useBetaFeatures();
  // Beta-gated: a non-beta org must never fire this request (mirrors
  // useRigFracSeriesColors.ts). Only used below to case-fold-resolve include
  // spellings and to complement an exclusion — a missing domain (gate off, or
  // in-flight) never blocks a filter update. Historical on AWS, so a
  // complement keeps values with no current activity (EVA-338); Azure keeps
  // the active-only domain it had before.
  const { data: valuesData } = useRigFracValuesQuery({
    enabled: hasFeature("Rig And Frac Layers"),
    historical: IS_AWS_ENV
  });

  const rawFiltersKey = useMemo(() => {
    const rig = buildSideFilters(
      layerFilterSettings[RIG_LAYER],
      constraints,
      valuesData?.values
    );
    const frac = buildSideFilters(
      layerFilterSettings[FRAC_LAYER],
      constraints,
      valuesData?.values
    );
    return JSON.stringify([rig, frac]);
  }, [layerFilterSettings, constraints, valuesData]);

  // Seed from THIS instance's own raw value, not the shared one: the shared
  // value is only advanced while at least one instance is mounted (see the
  // listener-count cleanup below), so with nothing mounted it can drift stale
  // against the store. Seeding from raw means a newly-mounted surface always
  // reflects the CURRENT store; the debounce below still coalesces in-flight
  // bursts across every mounted instance.
  const [filtersKey, setFiltersKey] = useState(() => rawFiltersKey);

  useEffect(() => {
    const listener = (key: string) => setFiltersKey(key);
    sharedFiltersKeyListeners.add(listener);
    return () => {
      sharedFiltersKeyListeners.delete(listener);
    };
  }, []);

  // Not cancelled on this instance's unmount — a per-instance cleanup here
  // would cancel the timer other still-mounted instances depend on.
  useEffect(() => {
    debouncedSetSharedFiltersKey(rawFiltersKey);
  }, [rawFiltersKey]);

  const { rigFilters, fracFilters } = useMemo(() => {
    const [rig, frac] = JSON.parse(filtersKey) as [RigFracFilters, RigFracFilters];
    return { rigFilters: rig, fracFilters: frac };
  }, [filtersKey]);

  return { rigFilters, fracFilters, filtersKey };
}
